import { DATE_CHANGE_REASONS, STAGE_INSTRUCTIONS_MAX, mdy } from "@fieldpath/shared";
import type {
  DateAuthority,
  DateChangeReason,
  ReferenceOption,
  TaskStageView,
} from "@fieldpath/shared";
import { useRef, useState } from "react";

import { DateHint, PAST_DATE_BOX } from "./DateHint";
import { continueList, FormatBar } from "./FormatBar";
import { RichText } from "./RichText";
import { PeoplePicker } from "./PeoplePicker";

interface Props {
  stage: TaskStageView;
  people: ReferenceOption[];
  /**
   * What the client was promised, when this task belongs to a deliverable.
   * Used to warn — never to prevent — when an internal date runs past it.
   */
  clientDueDate?: string | null;
  deliverableName?: string | null;
  busy: boolean;
  /**
   * Why the last save was refused (punch list F175, 1 Nov 2026). Shown at the
   * top of the form: the page's own error line is hidden while the form is
   * open, so a refused save looked like nothing happened.
   */
  error?: string | null;
  /**
   * What this person's change to the date would be (E5.2) — from the
   * server's `editability`. `direct`: they set it. `request`: they ask, and
   * the task's PM decides. `none`: it is shown, not offered.
   */
  dateAuthority: DateAuthority;
  /** Send an ask to move this stage's date (E5.2). */
  onRequestDate: (body: {
    date: string | null;
    reasonCategory?: DateChangeReason;
    note?: string | null;
  }) => void;
  onSave: (body: {
    description?: string | null;
    dueDate?: string | null;
    allocatedHours?: number | null;
    assigneeKeys?: string[];
    reasonCategory?: DateChangeReason;
    reasonNote?: string | null;
  }) => void;
  onCancel: () => void;
  /** The workflow requires hours on staffed stages (E7.3). */
  requiresHours: boolean;
  /** The workflow requires a due date on staffed stages (E11). */
  requiresDates: boolean;
  /**
   * The date, hours and people come from the task's resource-scheduler booking and are changed
   * there (21 Oct 2026). Shown, not offered; the instructions stay editable here.
   */
  fromBooking?: boolean;
  /**
   * May put somebody else on the stage or take them off (`editability.stageAssignOthers`:
   * the accountable people, 22 Oct 2026, or a GIS worker somebody else put on this
   * stage, 29 Oct 2026).
   * Anybody else adds themselves with "Add me to this stage".
   */
  mayAssignOthers: boolean;
  /**
   * May change the hours (`editability.stageWork`, punch list F143, 30 Oct
   * 2026): somebody on the stage, the task's PM / Task Manager or an admin.
   * Anybody else sees them, and their Save leaves them alone.
   */
  mayChangeWork: boolean;
  /**
   * May change the instructions (`editability.stageInstructions`): anybody
   * linked to Deltek since 3 Nov 2026 (Jeremy), except in a personal or the
   * template project, where it is the hours' people.
   */
  mayChangeInstructions: boolean;
}

/**
 * Editing one stage: who holds it, when it is due, and what they are meant to
 * do. The instructions field is the reason this model exists — it is what
 * people were creating a subtask per stage to get.
 */
export function StageEditor({
  stage,
  people,
  clientDueDate,
  deliverableName,
  busy,
  error = null,
  dateAuthority,
  onRequestDate,
  onSave,
  onCancel,
  requiresHours,
  requiresDates,
  fromBooking = false,
  mayAssignOthers,
  mayChangeWork,
  mayChangeInstructions,
}: Props) {
  const setsDate = dateAuthority === "direct";
  // E11: what the date field says, before a save is refused for it. A waiting
  // stage never needs one; the stage after it, not until work reaches it.
  const dateRequired = requiresDates && !stage.isEnd && stage.dateRule === null;
  const dueLabel = dateRequired ? "Due — required once someone is on the stage" : "Due";
  const dueHint = !requiresDates || stage.isEnd
    ? null
    : stage.dateRule === "waiting"
      ? "Waiting on the client or an agency: no date needed — the snooze carries the follow-up."
      : stage.dateRule === "after_waiting"
        ? "Follows a waiting stage, so it can be left blank while planning. It needs a date when the work arrives here."
        : null;
  const [description, setDescription] = useState(stage.description ?? "");
  const [dueDate, setDueDate] = useState(stage.dueDate ?? "");
  const [hours, setHours] = useState(
    stage.allocatedHours === null ? "" : String(stage.allocatedHours),
  );
  const [assignees, setAssignees] = useState<string[]>(
    stage.assignees.map((a) => a.key),
  );
  const [reasonCategory, setReasonCategory] = useState<DateChangeReason | "">("");
  const [reasonNote, setReasonNote] = useState("");
  const [reassigning, setReassigning] = useState(false);
  const [asking, setAsking] = useState(false);
  const descRef = useRef<HTMLTextAreaElement>(null);

  // A reason is only demanded when a date moves later. Setting one for the
  // first time is not a change, and pulling one in is good news.
  const pushingOut =
    stage.dueDate !== null && dueDate !== "" && dueDate > stage.dueDate;

  // Separate from pushingOut, and deliberately not a blocker. Work sometimes
  // genuinely is scheduled past the promise and the PM knows; what must not
  // happen is nobody noticing until the client does.
  const pastClientDate =
    clientDueDate != null && dueDate !== "" && dueDate > clientDueDate;

  const daysLate = pastClientDate
    ? Math.round(
        (new Date(`${dueDate}T00:00:00Z`).getTime() -
          new Date(`${clientDueDate}T00:00:00Z`).getTime()) /
          86400000,
      )
    : 0;

  const tooLong = description.length > STAGE_INSTRUCTIONS_MAX;

  const save = () => {
    // Only the instructions are this stage's to change here; the rest is the booking's.
    if (fromBooking) {
      onSave({ description: description.trim() === "" ? null : description });
      return;
    }
    // Hours and instructions each travel only from somebody who may change
    // them (F143; instructions since 3 Nov 2026); anybody else's Save
    // carries their place on the stage alone.
    onSave({
      ...(mayChangeInstructions ? { description: description.trim() === "" ? null : description } : {}),
      ...(mayChangeWork ? { allocatedHours: hours.trim() === "" ? null : Number(hours) } : {}),
      // The date travels with a save only from somebody who SETS it; anybody
      // else's Save never touches it (they ask instead, below).
      ...(setsDate ? { dueDate: dueDate === "" ? null : dueDate } : {}),
      assigneeKeys: assignees,
      ...(setsDate && pushingOut && reasonCategory
        ? { reasonCategory, reasonNote: reasonNote.trim() || null }
        : {}),
    });
  };

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--line)" }}>
      {error ? <div className="error" style={{ marginTop: 0 }}>{error}</div> : null}
      {fromBooking ? (
        <p className="muted" style={{ marginTop: 0 }}>
          This stage's date, hours and people come from the task's resource-scheduler booking.
          Change them in the scheduler; its instructions are edited here.
        </p>
      ) : null}
      {!mayChangeInstructions ? (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            Only someone on this stage, the task's PM / Task Manager, its project's PM or TM or an admin changes its
            instructions and hours. Add yourself to the stage first if the work is yours.
          </p>
          <div className="field">
            <label>Instructions for this stage</label>
            {stage.description ? (
              <RichText text={stage.description} />
            ) : (
              <span className="muted">No instructions for this stage yet.</span>
            )}
          </div>
        </>
      ) : (
      <div className="field">
        <label htmlFor={`desc-${stage.id}`}>Instructions for this stage</label>
        <FormatBar target={descRef} value={description} onChange={setDescription} />
        <textarea
          ref={descRef}
          id={`desc-${stage.id}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={(e) => {
            // Enter carries a list on to the next item, so a numbered list
            // is not a row of 1s and nobody has to press the button again.
            if (e.key !== "Enter" || e.shiftKey) return;
            const el = e.currentTarget;
            const next = continueList(description, el.selectionStart ?? 0);
            if (!next) return;
            e.preventDefault();
            setDescription(next.text);
            requestAnimationFrame(() => {
              el.setSelectionRange(next.caret, next.caret);
            });
          }}
          placeholder="What needs doing at this stage, and anything the person should know"
        />
        {/* Counted only near the ceiling: a count under every box is noise, and
            a refused save with no warning was how the old 10,000 was found. */}
        {description.length > STAGE_INSTRUCTIONS_MAX * 0.9 ? (
          <p
            className="muted"
            style={{ marginTop: 4, color: tooLong ? "var(--late)" : undefined }}
          >
            {description.length.toLocaleString()} of {STAGE_INSTRUCTIONS_MAX.toLocaleString()} characters
            {tooLong ? " — too long to save. Shorten it, or link to a document instead." : ""}
          </p>
        ) : null}

        {/* The formatted result, live.
            While editing you are looking at the marks — "**bold**" — which
            means nothing to most people until it is saved. Showing the
            outcome as it is typed removes the guesswork without a rich-text
            editor, and makes the asterisks read as something happening
            rather than something wrong. */}
        {description.trim() !== "" && hasMarkup(description) ? (
          <div className="preview">
            <span className="preview-tag">How it will look</span>
            <RichText text={description} />
          </div>
        ) : null}
      </div>
      )}

      {fromBooking ? (
        <div className="field">
          <label>Allocated hours, each</label>
          <span style={{ fontSize: 14 }}>{stage.allocatedHours === null ? "Not set" : `${stage.allocatedHours} h`} · from the booking</span>
        </div>
      ) : !mayChangeWork ? (
        <div className="field">
          <label>Allocated hours</label>
          <span style={{ fontSize: 14 }}>{stage.allocatedHours === null ? "Not set" : `${stage.allocatedHours} h`}</span>
          {/* Said here when the instructions above are open to this person
              and the hours are not (3 Nov 2026); otherwise the line at the
              top already says it. The grid's words for the same rule. */}
          {mayChangeInstructions ? (
            <p className="muted" style={{ margin: "4px 0 0" }}>
              Only someone on this stage, the task's PM / Task Manager, its project's PM or TM or an admin changes its
              hours. Add yourself to the stage first if the work is yours.
            </p>
          ) : null}
        </div>
      ) : (
      <div className="field">
        <label htmlFor={`hours-${stage.id}`}>
          {requiresHours ? "Allocated hours" : "Allocated hours — optional"}
        </label>
        <input
          id={`hours-${stage.id}`}
          type="number"
          min={0}
          step={0.25}
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          placeholder="How long this stage should take"
        />
        {/* WHAT 0 MEANS (E7.4). Null is "nobody estimated"; 0 is "deliberately
            no time" -- a stage that only waits on somebody outside. On an
            hours-required workflow it is how a waiting stage gets staffed at
            all, so the field says so before the save is refused for it. */}
        <p className="muted" style={{ marginTop: 4 }}>
          {requiresHours ? "Required once someone is on the stage. A stage that is only waiting — on the client, an agency — takes 0: deliberately no time." : "Blank means nobody has estimated it; 0 means deliberately no time."}
        </p>
      </div>
      )}

      {fromBooking ? (
        <div className="field">
          <label>{dueLabel}</label>
          <span style={{ fontSize: 14 }}>{stage.dueDate ? mdy(stage.dueDate) : "No date"} · the booking's last day</span>
        </div>
      ) : setsDate ? (
        <div className="field">
          <label htmlFor={`due-${stage.id}`}>{dueLabel}</label>
          <input
            id={`due-${stage.id}`}
            type="date"
            {...PAST_DATE_BOX}
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <DateHint />
          {dueHint ? (
            <p className="muted" style={{ marginTop: 4 }}>
              {dueHint}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="field">
          <label>{dueLabel}</label>
          <div className="row">
            <span style={{ fontSize: 14 }}>{stage.dueDate ? mdy(stage.dueDate) : "No date"}</span>
            {dateAuthority === "request" && !asking ? (
              <button type="button" onClick={() => setAsking(true)}>
                Request a change
              </button>
            ) : null}
          </div>
          {dateAuthority === "request" ? null : (
            <p className="muted" style={{ margin: "4px 0 0" }}>
              The task's PM / Task Manager or its project's PM or TM sets this date.
            </p>
          )}
        </div>
      )}

      {asking ? (
        <div className="rework-prompt">
          <div className="field">
            <label htmlFor={`ask-${stage.id}`}>
              Ask the task's PM to move this date to
            </label>
            <input
              id={`ask-${stage.id}`}
              type="date"
              {...PAST_DATE_BOX}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            <DateHint />
          </div>
          {pushingOut ? (
            <div className="field">
              <label htmlFor={`askwhy-${stage.id}`}>
                It moves later, from {stage.dueDate && mdy(stage.dueDate)} to {mdy(dueDate)}. Why?
              </label>
              <select
                id={`askwhy-${stage.id}`}
                value={reasonCategory}
                onChange={(e) => setReasonCategory(e.target.value as DateChangeReason)}
              >
                <option value="">Choose a reason…</option>
                {DATE_CHANGE_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="field">
            <label htmlFor={`asknote-${stage.id}`}>Note for the PM (optional)</label>
            <input
              id={`asknote-${stage.id}`}
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
              placeholder="What changed"
            />
          </div>
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => {
                setAsking(false);
                setDueDate(stage.dueDate ?? "");
              }}
              disabled={busy}
            >
              Never mind
            </button>
            <button
              type="button"
              className="primary"
              disabled={busy || dueDate === (stage.dueDate ?? "") || (pushingOut && !reasonCategory)}
              onClick={() => {
                onRequestDate({
                  date: dueDate === "" ? null : dueDate,
                  ...(pushingOut && reasonCategory ? { reasonCategory } : {}),
                  note: reasonNote.trim() || null,
                });
                setAsking(false);
              }}
            >
              Send request
            </button>
          </div>
        </div>
      ) : null}

      {pastClientDate ? (
        <div className="warn">
          This is {daysLate} {daysLate === 1 ? "day" : "days"} after the client due
          date{deliverableName ? ` for “${deliverableName}”` : ""} ({clientDueDate && mdy(clientDueDate)}).
          You can still set it.
        </div>
      ) : null}

      {setsDate && pushingOut ? (
        <>
          <div className="field">
            <label htmlFor={`why-${stage.id}`}>
              This date is moving from {stage.dueDate && mdy(stage.dueDate)} to {mdy(dueDate)}. Why?
            </label>
            <select
              id={`why-${stage.id}`}
              value={reasonCategory}
              onChange={(e) => setReasonCategory(e.target.value as DateChangeReason)}
              required
            >
              <option value="">Choose a reason…</option>
              {DATE_CHANGE_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor={`note-${stage.id}`}>Detail (optional)</label>
            <input
              id={`note-${stage.id}`}
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
              placeholder="Anything worth knowing later"
            />
          </div>
        </>
      ) : null}

      {/* The people list stays shut unless asked for. Stage assignees come
          from the roles chosen when the task was created, so most of the time
          there is nothing to change here and a permanently open list of
          thirty-four names is just noise. */}
      {reassigning ? (
        <PeoplePicker
          label="Assigned to"
          people={people}
          selected={assignees}
          onChange={setAssignees}
        />
      ) : (
        <div className="field">
          <label>Assigned to</label>
          <div className="row">
            <span style={{ fontSize: 14 }}>
              {stage.assignees.length > 0
                ? stage.assignees.map((a) => a.label).join(", ")
                : "Nobody"}
            </span>
            {fromBooking ? (
              <span className="muted" style={{ fontSize: 13 }}>the booking's crew</span>
            ) : mayAssignOthers ? (
              <button type="button" onClick={() => setReassigning(true)}>
                Reassign
              </button>
            ) : null}
          </div>
          {!fromBooking && !mayAssignOthers ? (
            <p className="muted" style={{ margin: "4px 0 0" }}>
              Only the task's PM / Task Manager, its deliverable's PM, the project's PM or TM, an admin
              or GIS staff someone else put on this stage changes who is on it.
            </p>
          ) : null}
        </div>
      )}

      <div className="row">
        <button
          className="primary"
          onClick={save}
          disabled={busy || tooLong || (setsDate && pushingOut && !reasonCategory)}
        >
          Save stage
        </button>
        <button onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Whether the text contains anything the preview would change.
 *
 * Plain prose needs no preview — showing an identical copy of what someone
 * just typed is clutter that teaches them to ignore the panel, so by the
 * time it matters they have stopped looking at it.
 */
function hasMarkup(text: string): boolean {
  return (
    /\*\*?[^*\n]+\*\*?/.test(text) ||
    /`[^`\n]+`/.test(text) ||
    /^\s*[-*]\s+/m.test(text) ||
    /^\s*\d+[.)]\s+/m.test(text) ||
    /\[[^\]\n]+\]\([^)\s]+\)/.test(text) ||
    /(?:https?:\/\/|www\.)\S+/.test(text)
  );
}
